import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CircleCheckBig,
  Clock3,
  Copy,
  Download,
  Eye,
  Pencil,
  Plus,
  Search,
  SquarePen,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { ListExportDialog } from '@/components/shared'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import {
  QuoteStatusBadge,
  ClientNameWithProspect,
} from '@/components/shared/StatusBadges'
import { useQuotesList, useQuotesSummary } from '@/hooks/useQuotes'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import { useUrlListParams } from '@/hooks/useUrlListParams'
import { formatCurrency, formatDate } from '@/lib/format'
import { quotesApi } from '@/services/quotes'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { DeleteQuoteDialog } from '@/components/billing/DeleteQuoteDialog'
import { JobDetailDrawer } from '@/components/jobs/JobDetailDrawer'
import { cn } from '@/lib/utils'
import { useTopBarActions } from '@/contexts/TopBarActionsContext'
import {
  quoteInterventionReference,
  portalQuoteStatus,
  quoteGroupForKpi,
  quoteTitle,
  type PortalQuoteStatus,
} from '@/lib/quotes'
import { type Quote, type QuoteListQuery, type QuoteSummary } from '@/types'

const filterTriggerClass = cn(
  'h-10 w-auto shrink-0 min-w-[127px] justify-between gap-2 rounded-xl border border-[#e7e9ef] bg-white py-0 pl-4 pr-3 text-sm font-normal leading-none text-[#171a20] data-[state=open]:ring-0 [&>span]:line-clamp-none [&>span]:whitespace-nowrap [&_svg]:size-4',
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS
)

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium uppercase leading-[14px] tracking-normal text-[#3d434e]'

const tdClass =
  'h-20 overflow-hidden border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm last:border-b-0'
const tableValueTextClass = 'text-sm font-medium leading-[1.4] text-[#171a20]'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'

const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'

const actionsGroupClass =
  'ml-auto flex w-[152px] items-center justify-end gap-2'

const actionIconClass = 'h-4 w-4 shrink-0'

const topBarAddQuoteClass =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

const summaryCardInteractionClass =
  'min-w-0 rounded-2xl border p-4 text-left shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-12px_rgba(23,26,32,0.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'

function fromCsv(value: string | undefined): string[] {
  return value ? value.split(',').filter(Boolean) : []
}

function toCsv(values: string[]): string | undefined {
  return values.length ? values.join(',') : undefined
}
const summaryCardActiveColor = '#ff5a1f'

const CARD_THEMES = {
  waiting: {
    border: '#fde68a',
    tint: '#fbf3d9',
    iconBg: '#ffffff',
    icon: '#ce7e0a',
    value: '#171a20',
    label: '#5a5a5a',
  },
  expired: {
    border: '#fca5a5',
    tint: '#fde9e8',
    iconBg: '#fecaca',
    icon: '#d63b33',
    value: '#e5484d',
    label: '#5a5a5a',
  },
  won: {
    border: '#a7f3d0',
    tint: '#ecfdf5',
    iconBg: '#ffffff',
    icon: '#059669',
    value: '#171a20',
    label: '#5a5a5a',
  },
  signature_rate: {
    border: '#e7e9ef',
    tint: '#f4f5f8',
    iconBg: '#f4f5f8',
    icon: '#272c36',
    value: '#171a20',
    label: '#5a5a5a',
  },
} as const

/** Figma 323:33463 column widths (9 columns, 1172px table). */
const COL = {
  clientName: 'w-[176px] min-w-[176px] text-left',
  title: 'w-[140px] min-w-[140px] text-left',
  totalAmount: 'w-[110px] min-w-[110px] text-center',
  status: 'w-[110px] min-w-[110px] text-center',
  createdDate: 'w-[110px] min-w-[110px] text-left',
  technician: 'w-[110px] min-w-[110px] text-left',
  reference: 'w-[120px] min-w-[120px] text-left',
  intervention: 'w-[176px] min-w-[176px] text-left',
  actions: 'w-[176px] min-w-[176px] max-w-[176px] text-right',
} as const

const TABLE_MIN_WIDTH = 1172
const COLUMN_COUNT = 9

const STATUS_OPTIONS: { value: PortalQuoteStatus; labelKey: string }[] = [
  { value: 'draft', labelKey: 'list.status.draft' },
  { value: 'sent', labelKey: 'list.status.sent' },
  { value: 'won', labelKey: 'list.status.won' },
  { value: 'lost', labelKey: 'list.status.lost' },
]

const CARDS = [
  {
    key: 'waiting',
    labelKey: 'list.cards.awaitingResponse',
    icon: Clock3,
    theme: CARD_THEMES.waiting,
  },
  {
    key: 'expires_soon',
    labelKey: 'list.cards.expiresSoon',
    icon: AlertTriangle,
    theme: CARD_THEMES.expired,
  },
  {
    key: 'won',
    labelKey: 'list.cards.won',
    icon: CircleCheckBig,
    theme: CARD_THEMES.won,
  },
  {
    key: 'signature_rate',
    labelKey: 'list.cards.signatureRate',
    icon: Pencil,
    theme: CARD_THEMES.signature_rate,
  },
] as const

type QuoteCardKey = (typeof CARDS)[number]['key']

function isPortalQuoteStatus(value: string): value is PortalQuoteStatus {
  return STATUS_OPTIONS.some(option => option.value === value)
}

function statusFilterForCard(key: QuoteCardKey): PortalQuoteStatus | undefined {
  switch (key) {
    case 'waiting':
    case 'expires_soon':
      return 'sent'
    case 'won':
    case 'signature_rate':
      return 'won'
  }
}

// 2.5.1 Quote list - searchable, filterable, paginated with summary cards.
export default function QuotesListPage() {
  const { t } = useTranslation('quotes')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  } = useUrlListParams<{ status?: string; quote_group?: string }>({})

  const selectedStatusFilters = useMemo(
    () => fromCsv(filters.status).filter(isPortalQuoteStatus),
    [filters.status]
  )
  const apiQuery = useMemo<QuoteListQuery>(() => {
    const { status: _status, ...listQuery } = query
    void _status
    const apiStatuses = selectedStatusFilters.filter(isPortalQuoteStatus)

    return {
      ...listQuery,
      status: apiStatuses.length ? apiStatuses : undefined,
    }
  }, [query, selectedStatusFilters])

  const { data, isLoading, isError } = useQuotesList(apiQuery)
  const { data: summary } = useQuotesSummary()
  const [deleteTarget, setDeleteTarget] = useState<Quote | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)

  const quotes = data?.data ?? []
  const counts = summary?.data ?? null
  const totalRows = data?.metadata?.total ?? quotes.length

  const topBarAction = useMemo(
    () => (
      <Permit module='quotes' action='edit'>
        <Button
          className={topBarAddQuoteClass}
          onClick={() => navigate('/quotes/new')}
        >
          <Plus className='size-4' />
          {t('list.createQuote')}
        </Button>
      </Permit>
    ),
    [navigate, t]
  )
  useTopBarActions(topBarAction)

  const onDuplicate = async (q: Quote) => {
    try {
      const res = await quotesApi.duplicate(q.id)
      invalidatePortalMetricQueries(queryClient)
      toast({ variant: 'success', title: t('list.duplicateSuccess') })
      if (res.data?.id) navigate(`/quotes/${res.data.id}/edit`)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('list.duplicateError')),
      })
    }
  }

  return (
    <div className='-m-6 flex min-h-full flex-col gap-6 bg-[#f4f5f8] p-6'>
      <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-4'>
        {CARDS.map(c => {
          const active = filters.quote_group === c.key
          const Icon = c.icon
          const data = counts?.[c.key as keyof QuoteSummary]
          const main =
            c.key === 'signature_rate'
              ? `${counts?.signature_rate.percentage ?? 0}%`
              : t('list.cards.count', {
                  count: data && 'count' in data ? data.count : 0,
                })
          const sub =
            c.key === 'signature_rate'
              ? t('list.cards.signatureSubtext', {
                  count: counts?.signature_rate.issued_count ?? 0,
                })
              : formatCurrency(
                  data && 'total' in data ? data.total : 0,
                  'EUR',
                  2,
                  'suffix'
                )

          return (
            <button
              key={c.key}
              type='button'
              onClick={() =>
                setFilters({
                  ...quoteGroupForKpi(c.key),
                  status: statusFilterForCard(c.key),
                })
              }
              className={cn(summaryCardInteractionClass)}
              style={{
                borderColor: active ? summaryCardActiveColor : c.theme.border,
                backgroundColor:
                  c.key === 'signature_rate' ? '#ffffff' : c.theme.tint,
              }}
            >
              <div className='flex min-h-[74px] items-center gap-4'>
                <span
                  className='flex size-10 shrink-0 items-center justify-center rounded-xl shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'
                  style={{ backgroundColor: c.theme.iconBg }}
                >
                  <Icon className='size-4' style={{ color: c.theme.icon }} />
                </span>
                <div className='flex min-w-0 flex-1 flex-col'>
                  <p
                    className='font-heading text-xs font-medium leading-[1.2]'
                    style={{ color: c.theme.label }}
                  >
                    {t(c.labelKey)}
                  </p>
                  <p
                    className='mt-2 font-heading text-2xl font-semibold leading-[1.2] tracking-normal'
                    style={{ color: c.theme.value }}
                  >
                    {main}
                  </p>
                  <p className='mt-1 truncate text-xs font-medium leading-4 text-[#5a5a5a]'>
                    {sub}
                  </p>
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <div className='flex shrink-0 flex-col gap-4 overflow-visible lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center'>
          <div className='relative w-full shrink-0 sm:w-[280px]'>
            <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#272c36]' />
            <Input
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder={t('list.searchPlaceholder')}
              maxLength={100}
              className={cn(
                'h-10 w-full rounded-xl border-[#e7e9ef] bg-white pl-10 pr-4 text-sm font-normal leading-none text-[#171a20] placeholder:text-[#3d434e]',
                FORM_CONTROL_FOCUS_CLASS,
                FORM_FILTER_HOVER_CLASS
              )}
            />
          </div>
          <MultiSelectFilter
            options={STATUS_OPTIONS.map(o => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
            selected={selectedStatusFilters}
            onChange={values => {
              const selectedValues = values.filter(isPortalQuoteStatus)

              setFilters({
                status: toCsv(selectedValues),
                quote_group: undefined,
              })
            }}
            placeholder={t('list.statusPlaceholder')}
            selectedLabel={count => t('list.statusFilterSelected', { count })}
            clearLabel={t('list.allStatuses')}
            emptyLabel={t('list.statusFilterEmpty')}
            triggerClass={filterTriggerClass}
          />
        </div>

        <div className='flex w-full shrink-0 justify-end lg:w-auto'>
          <Permit module='quotes' action='view'>
            <Button
              type='button'
              variant='outline'
              className='h-10 rounded-xl border-[#e7e9ef] bg-white px-4 text-sm font-normal text-[#171a20] hover:bg-[#f4f5f8]'
              onClick={() => setExportOpen(true)}
            >
              <Download className='mr-2 size-4' />
              {t('list.export')}
            </Button>
          </Permit>
        </div>
      </div>

      <Card className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_-4px_rgba(38,45,66,0.05),0_8px_16px_-4px_rgba(38,45,66,0.1)]'>
        <div className='overflow-x-auto'>
          <table
            className='w-full table-fixed caption-bottom text-sm'
            style={{ minWidth: TABLE_MIN_WIDTH }}
          >
            <TableHeader className='sticky top-0 z-10 bg-[#fbfbfc]'>
              <TableRow className='border-0'>
                <TableHead className={cn(thClass, COL.clientName)}>
                  {t('list.columns.clientName')}
                </TableHead>
                <TableHead className={cn(thClass, COL.title)}>
                  {t('list.columns.title')}
                </TableHead>
                <TableHead className={cn(thClass, COL.totalAmount)}>
                  {t('list.columns.totalAmount')}
                </TableHead>
                <TableHead className={cn(thClass, COL.status)}>
                  {t('list.columns.status')}
                </TableHead>
                <TableHead className={cn(thClass, COL.createdDate)}>
                  {t('list.columns.createdDate')}
                </TableHead>
                <TableHead className={cn(thClass, COL.technician)}>
                  {t('list.columns.technician')}
                </TableHead>
                <TableHead className={cn(thClass, COL.reference)}>
                  {t('list.columns.reference')}
                </TableHead>
                <TableHead className={cn(thClass, COL.intervention)}>
                  {t('list.columns.intervention')}
                </TableHead>
                <TableHead className={cn(thClass, COL.actions)}>
                  <div className={actionsGroupClass}>
                    {t('list.columns.actions')}
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-destructive'
                    )}
                  >
                    {t('list.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && quotes.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-10 text-center text-muted-foreground'
                    )}
                  >
                    {t('list.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                quotes.map(q => {
                  const portalStatus = portalQuoteStatus(q.status)
                  const hasConverted =
                    !!q.converted_invoice_id || !!q.converted_contract_id
                  const editDisabled = portalStatus === 'lost' || hasConverted
                  const deleteDisabled = hasConverted
                  const title = quoteTitle(q)
                  const interventionReference = quoteInterventionReference(q)

                  return (
                    <TableRow
                      key={q.id}
                      className='group cursor-pointer transition-colors'
                      onClick={() => navigate(`/quotes/${q.id}`)}
                    >
                      <TableCell
                        className={cn(
                          tdClass,
                          COL.clientName,
                          'relative before:absolute before:bottom-0 before:left-0 before:top-0 before:w-1 before:bg-primary before:opacity-0 before:transition-opacity group-hover:before:opacity-100'
                        )}
                      >
                        <div className='min-w-0 text-sm font-medium leading-[1.4] text-[#171a20]'>
                          <ClientNameWithProspect
                            name={q.client?.name ?? q.client_name ?? '—'}
                            category={q.client?.category}
                          />
                        </div>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.title)}>
                        <TruncatedTextTooltip
                          className='line-clamp-2 text-sm font-normal leading-[1.4] text-[#171a20]'
                          content={title}
                        >
                          {title}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.totalAmount)}>
                        <span className={tableValueTextClass}>
                          {formatCurrency(q.total, 'EUR', 2, 'suffix')}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.status)}>
                        <QuoteStatusBadge status={q.status} />
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.createdDate)}>
                        <span className='text-sm font-normal leading-[1.4] text-[#3d434e]'>
                          {formatDate(q.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.technician)}>
                        <TruncatedTextTooltip
                          className='line-clamp-2 text-sm font-normal leading-[1.4] text-[#3d434e]'
                          content={q.technician_name ?? undefined}
                        >
                          {q.technician_name ?? '--'}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.reference)}>
                        <TruncatedTextTooltip
                          className={cn('line-clamp-2', tableValueTextClass)}
                          content={q.number ?? undefined}
                        >
                          {q.number ?? '—'}
                        </TruncatedTextTooltip>
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.intervention)}>
                        {q.job_id ? (
                          <button
                            type='button'
                            className='block min-w-0 max-w-full text-left text-sm font-medium leading-[1.4] hover:text-[#ff5a1f] hover:underline'
                            onClick={event => {
                              event.stopPropagation()
                              setSelectedJobId(q.job_id ?? null)
                            }}
                          >
                            <TruncatedTextTooltip
                              className='line-clamp-2'
                              content={interventionReference ?? undefined}
                            >
                              {interventionReference}
                            </TruncatedTextTooltip>
                          </button>
                        ) : (
                          <span className='text-sm font-medium leading-[1.4] text-[#3d434e]'>
                            —
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={cn(tdClass, COL.actions)}>
                        <div className={actionsGroupClass}>
                          <TruncatedTextTooltip
                            content={t('list.view')}
                            delayDuration={0}
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                          >
                            <button
                              type='button'
                              className={actionBtnClass}
                              aria-label={t('list.view')}
                              onClick={event => {
                                event.stopPropagation()
                                navigate(`/quotes/${q.id}`)
                              }}
                            >
                              <Eye className={actionIconClass} />
                            </button>
                          </TruncatedTextTooltip>
                          <Permit module='quotes' action='edit'>
                            <TruncatedTextTooltip
                              content={t('list.edit')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  'disabled:pointer-events-none disabled:text-[#8b93a3]'
                                )}
                                aria-label={t('list.edit')}
                                disabled={editDisabled}
                                onClick={event => {
                                  event.stopPropagation()
                                  navigate(`/quotes/${q.id}/edit`)
                                }}
                              >
                                <SquarePen className={actionIconClass} />
                              </button>
                            </TruncatedTextTooltip>
                          </Permit>
                          <Permit module='quotes' action='edit'>
                            <TruncatedTextTooltip
                              content={t('list.duplicate')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={actionBtnClass}
                                aria-label={t('list.duplicate')}
                                onClick={event => {
                                  event.stopPropagation()
                                  void onDuplicate(q)
                                }}
                              >
                                <Copy className={actionIconClass} />
                              </button>
                            </TruncatedTextTooltip>
                          </Permit>
                          <Permit module='quotes' action='delete'>
                            <TruncatedTextTooltip
                              content={t('list.delete')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  destructiveActionBtnClass,
                                  'disabled:pointer-events-none disabled:text-[#8b93a3]'
                                )}
                                aria-label={t('list.delete')}
                                disabled={deleteDisabled}
                                onClick={event => {
                                  event.stopPropagation()
                                  setDeleteTarget(q)
                                }}
                              >
                                <Trash2 className={actionIconClass} />
                              </button>
                            </TruncatedTextTooltip>
                          </Permit>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </table>
        </div>

        <ListPagination
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          showSummary={false}
        />
      </Card>

      <DeleteQuoteDialog
        quote={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => setDeleteTarget(null)}
      />

      <ListExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        entityLabel='Quote'
        title={t('export.title')}
        rowCount={totalRows}
        onExport={async format => {
          const res = await quotesApi.export({
            ...apiQuery,
            format,
          })
          return {
            url: res.data!.url,
            filename: res.data!.filename,
          }
        }}
      />

      <JobDetailDrawer
        jobId={selectedJobId}
        onClose={() => setSelectedJobId(null)}
      />
    </div>
  )
}
